import { Badge, Box, FlexLayout, H4, Text } from '@deity/falcon-ui';
import type { IconType } from '@deity-io/portal-ui';
import { Icon } from '@deity-io/portal-ui';
import { ConfirmationModal } from 'components';
import { toLocalDateString } from 'contracts/constants';
import type { PopoverActionMenuOption } from 'contracts/interfaces/PopoverActionMenuOption';
import type { FC } from 'react';
import { useState } from 'react';
import { useDeletePackageMutation, useGetPackageContentInfoQuery } from 'services/graphql/generated/hooks';
import MoreButton from './MoreButton';

type PackageItemProps = {
  uuid: string;
  name: string;
  modifiedAt: string;
  modifiedBy: string;
};

export const PackageItem: FC<PackageItemProps> = ({ uuid, name, modifiedAt, modifiedBy }) => {
  const { data } = useGetPackageContentInfoQuery({
    variables: { packageUuid: uuid }
  });

  const [deletePackageMutation, { loading: loadingDeletedPackage, error: errorDeletedPackage }] =
    useDeletePackageMutation({
      variables: {
        uuid
      },
      refetchQueries: ['GetAllPackages']
    });
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const handleConfirmationModalConfirm = () => {
    deletePackageMutation();
  };

  const onMoreButtonActionSelect = (action: string) => {
    const actions = {
      delete: () => {
        setIsConfirmationModalOpen(true);
      }
    };
    actions[action]();
  };

  const moreButtonActionOptions: PopoverActionMenuOption<IconType, string, string>[] = [
    { text: 'Delete', icon: 'delete', action: 'delete' }
  ];

  return (
    <>
      <Box>
        <H4 fontSize="lg" color="primary" fontWeight="bold">
          {name}
        </H4>
      </Box>
      <Badge fontSize="sm" fontWeight="bold" style={{ backgroundColor: '#f7d677', color: '#33260b', letterSpacing: 0 }}>
        {data?.getPackageContentInfo.latestStatus}
      </Badge>
      <H4 style={{ justifySelf: 'center' }}>{data?.getPackageContentInfo.availableVersions}</H4>
      <FlexLayout alignItems="center" justifySelf="center">
        <H4 fontSize="lg" color="primaryText" mr="xs">
          {data?.getPackageContentInfo.publishedVersions} x
        </H4>
        <Icon icon="cloud" />
      </FlexLayout>
      <H4 fontSize="lg" color="primaryText">
        {modifiedAt ? toLocalDateString(modifiedAt) : ''}
      </H4>
      <H4 fontSize="lg" color="primaryText">
        John Smith
      </H4>
      <Box justifySelf="flex-end">
        <MoreButton options={moreButtonActionOptions} onSelect={onMoreButtonActionSelect} />
        {errorDeletedPackage && (
          <Text
            color="error"
            fontSize="xs"
            css={{
              position: 'absolute',
              bottom: 5,
              right: 5
            }}
          >
            Error when deleting a package. Please try again.
          </Text>
        )}
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          entityType="package"
          entityUuid={uuid}
          actionType="delete"
          confirmText="Delete package"
          loading={loadingDeletedPackage}
          onConfirm={handleConfirmationModalConfirm}
        />
      </Box>
    </>
  );
};
